import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import PageContainer from "@/components/PageContainer";
import {
  getResetCount,
  getWeekActivity,
  getRecentWins,
  getTopStates,
  getTopInterventions,
  getTimeOfDayPattern,
} from "@/lib/resetLog";
import { getMoments } from "@/lib/storage";
import { RESET_STATES, getInterventionById } from "@/lib/resetData";
import { usePremium } from "@/lib/premium";
import { track } from "@/lib/analytics";

const stateLabel = (id) => RESET_STATES.find((s) => s.id === id)?.label || id;

export default function Progress() {
  const nav = useNavigate();
  const { premium } = usePremium();

  const resetCount = useMemo(() => getResetCount(), []);
  const momentCount = useMemo(() => getMoments().length, []);
  const week = useMemo(() => getWeekActivity(), []);
  const wins = useMemo(() => getRecentWins(5), []);
  const topStates = useMemo(() => getTopStates(), []);
  const topInterventions = useMemo(() => getTopInterventions(), []);
  const timeOfDay = useMemo(() => getTimeOfDayPattern(), []);

  const weekMax = Math.max(1, ...week.map((d) => d.count));
  const navigated = resetCount + momentCount;

  return (
    <PageContainer testid="progress-page" className="pt-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1
          className="text-[30px] leading-tight"
          style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}
        >
          Patterns over time
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          This is a record of moments you chose to notice or respond to. It does not
          grade how they went.
        </p>
      </motion.div>

      {/* Hero counters */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div data-testid="progress-resets-count" className="p-5 rounded-3xl" style={{ background: "var(--un-bg-2)" }}>
          <p className="text-[30px] font-light" style={{ color: "var(--un-plum)", fontFamily: "Manrope, sans-serif" }}>
            {resetCount}
          </p>
          <p className="text-[12px] mt-1" style={{ color: "var(--un-text-3)" }}>
            resets completed
          </p>
        </div>
        <div data-testid="progress-moments-count" className="p-5 rounded-3xl" style={{ background: "var(--un-bg-2)" }}>
          <p className="text-[30px] font-light" style={{ color: "var(--un-plum)", fontFamily: "Manrope, sans-serif" }}>
            {navigated}
          </p>
          <p className="text-[12px] mt-1" style={{ color: "var(--un-text-3)" }}>
            difficult moments navigated
          </p>
        </div>
      </div>

      {/* This week */}
      <div className="mt-4 p-5 rounded-3xl" style={{ background: "var(--un-bg-2)" }}>
        <p className="text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
          This week
        </p>
        <div className="flex items-end justify-between gap-2 h-[72px]" data-testid="progress-week-bars">
          {week.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
              <div
                className="w-full rounded-full"
                style={{
                  height: `${Math.max(8, (d.count / weekMax) * 56)}px`,
                  background: d.count
                    ? "linear-gradient(180deg, #D6B884 0%, #C4A16A 100%)"
                    : "rgba(91,57,84,0.10)",
                }}
              />
              <span className="text-[10px]" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
                {d.day}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent moments */}
      {wins.length > 0 && (
        <div className="mt-4 p-5 rounded-3xl" style={{ background: "var(--un-bg-2)" }}>
          <p className="text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
            Recent moments
          </p>
          <div className="flex flex-col gap-2.5">
            {wins.map((w) => {
              const r = getInterventionById(w.interventionId);
              return (
                <div key={w.id} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#C4A16A" }} />
                  <p className="text-[13px] flex-1" style={{ color: "var(--un-text-2)" }}>
                    {r?.title || w.interventionId}
                    {w.state ? ` · ${stateLabel(w.state).toLowerCase()}` : ""}
                  </p>
                  <span className="text-[11px]" style={{ color: "var(--un-text-3)" }}>
                    {new Date(w.completedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insights — Premium */}
      <div className="mt-4 mb-4">
        {premium ? (
          <div data-testid="progress-insights" className="p-5 rounded-3xl" style={{ background: "linear-gradient(135deg, rgba(196,161,106,0.12) 0%, var(--un-bg-2) 60%)" }}>
            <p className="text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
              Your patterns · last 30 days
            </p>
            {topStates.length === 0 ? (
              <p className="text-[13px]" style={{ color: "var(--un-text-3)" }}>
                Complete a few resets and your patterns will gently appear here.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-[12px] font-medium mb-1.5" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
                    Most common moments
                  </p>
                  {topStates.slice(0, 3).map((s) => (
                    <p key={s.state} className="text-[13px]" style={{ color: "var(--un-text-3)" }}>
                      {stateLabel(s.state)} · {s.count}×
                    </p>
                  ))}
                </div>
                <div>
                  <p className="text-[12px] font-medium mb-1.5" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
                    What helps you most
                  </p>
                  {topInterventions.slice(0, 3).map((t) => (
                    <p key={t.interventionId} className="text-[13px]" style={{ color: "var(--un-text-3)" }}>
                      {getInterventionById(t.interventionId)?.title || t.interventionId}
                      {t.helped > 0 ? ` · helped ${t.helped}×` : ` · used ${t.count}×`}
                    </p>
                  ))}
                </div>
                <div>
                  <p className="text-[12px] font-medium mb-1.5" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
                    When you reach for support
                  </p>
                  <p className="text-[13px]" style={{ color: "var(--un-text-3)" }}>
                    {Object.entries(timeOfDay)
                      .filter(([, v]) => v > 0)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, v]) => `${k} ${v}×`)
                      .join(" · ") || "Not enough yet — that's okay."}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            data-testid="progress-insights-locked"
            onClick={() => {
              track("paywall_viewed", { source: "progress" });
              nav("/premium?source=progress");
            }}
            className="w-full un-press p-5 rounded-3xl text-left"
            style={{ background: "linear-gradient(135deg, rgba(196,161,106,0.14) 0%, rgba(91,57,84,0.08) 100%)" }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Lock size={13} style={{ color: "var(--un-plum)" }} />
              <p className="text-[13px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
                Pattern views
              </p>
            </div>
            <p className="text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Patterns in the moments you saved, the techniques you return to, and
              when you tend to reach for support — with Premium.
            </p>
          </button>
        )}
      </div>

      {/* Link to legacy saved dashboard */}
      <button
        data-testid="progress-dashboard-link"
        onClick={() => nav("/dashboard")}
        className="mb-10 w-full un-press p-4 rounded-3xl flex items-center justify-between"
        style={{ background: "var(--un-bg-2)" }}
      >
        <span className="text-[13px]" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
          Saved reflections, actions & moments
        </span>
        <ArrowRight size={15} style={{ color: "var(--un-text-3)" }} />
      </button>
    </PageContainer>
  );
}
